import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';

import { AdminReportsPort } from '../../../app/ports/reports/admin-reports.port';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ReportSdk } from 'lib-klab-sdks';
import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';

import { AdminTimesheetRequest } from '../../../domain/models/reports/request/admin-timesheet.request';
import {
    AdminExportFormat,
    AdminTimesheetExportRequest,
    AdminDownloadListExportRequest,
    AdminDownloadEmployeeExportRequest,
    AdminProductivityExportRequest,
    AdminActivityExportRequest,
} from '../../../domain/models/reports/request/admin-export.request';
import { AdminDownloadRequest } from '../../../domain/models/reports/request/admin-download.request';
import { AdminDownloadEmployeeRequest } from '../../../domain/models/reports/request/admin-download-employee.request';
import { AdminProductivityRequest } from '../../../domain/models/reports/request/admin-productivity.request';
import { AdminTimesheetResponse } from '../../../domain/models/reports/admin-timesheet-response.model';
import { AdminDownloadResponse } from '../../../domain/models/reports/admin-download-response.model';
import { AdminDownloadEmployeeResponse } from '../../../domain/models/reports/admin-download-employee-response.model';
import { EmployeeProductivityResponse } from '../../../domain/models/reports/employee-productivity-response.model';
import { OrganizationProductivityResponse } from '../../../domain/models/reports/organization-productivity-response.model';
import { EmployeeProductivityMetricResponse } from '../../../domain/models/reports/employee-productivity-metric-response.model';
import { LocationProductivityMetricResponse } from '../../../domain/models/reports/location-productivity-metric-response.model';
import { EmployeeDailyMetricResponse } from '../../../domain/models/reports/employee-daily-metric-response.model';
import { AdminActivitySummaryRequest } from '../../../domain/models/reports/request/admin-activity-summary.request';
import { AdminActivityDetailRequest } from '../../../domain/models/reports/request/admin-activity-detail.request';
import { AdminActivitySummaryResponse } from '../../../domain/models/reports/admin-activity-summary-response.model';
import { AdminActivityDetailResponse } from '../../../domain/models/reports/admin-activity-detail-response.model';
import {
    mapSdkAdminTimesheetResponseToDomain,
    mapSdkAdminDownloadResponseToDomain,
    mapSdkEmployeeProductivityResponseToDomain,
    mapSdkOrganizationProductivityResponseToDomain,
    mapSdkAdminDownloadEmployeeResponseToDomain,
    mapSdkEmployeeProductivityMetricResponseToDomain,
    mapSdkLocationProductivityMetricResponseToDomain,
    mapSdkEmployeeDailyMetricResponseToDomain,
} from '../../mappers/reports/admin-reports.mapper';
import {
    toSdkAdminActivitySummaryRequest,
    toSdkAdminActivityDetailRequest,
    toSdkAdminActivityExportRequest,
    mapSdkAdminActivitySummaryResponseToDomain,
    mapSdkAdminActivityDetailResponseToDomain,
} from '../../mappers/reports/admin-activity-reports.mapper';
import { adminReportsSdkMock } from '../../mocks/report/admin-reports.mock';

type AdminReportsMockAction =
    | 'getAdminTimesheetsList'
    | 'getAdminDownloadList'
    | 'getAdminDownloadEmployee'
    | 'getEmployeeProductivity'
    | 'getOrganizationProductivity'
    | 'getEmployeeProductivityMetrics'
    | 'getOrganizationProductivityMetrics'
    | 'exportAdminTimesheetsList'
    | 'exportAdminDownloadList'
    | 'exportAdminDownloadEmployee'
    | 'exportProductivityEmployees'
    | 'exportProductivityDepartment'
    | 'exportProductivityLocation'
    | 'exportProductivityAll'
    | 'exportWeeklyProductivity'
    | 'getEmployeeDailyProductivity'
    | 'getEmployeeDailyMetrics'
    | 'getAdminActivitiesSummary'
    | 'getAdminActivityDetail'
    | 'exportAdminActivities';

@Injectable({ providedIn: 'root' })
export class AdminReportsPortAdapter implements AdminReportsPort {
    constructor(
        private http: HttpClient,
        private adminService: ReportSdk.AdminService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getAdminTimesheetsList(payload: AdminTimesheetRequest): Observable<AdminTimesheetResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-timesheets');

        const sdkPayload: ReportSdk.AdminTimesheetRequest = {
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
            ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
            ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
            ...(payload.search ? { search: payload.search } : {}),
            ...(payload.page != null ? { page: payload.page } : {}),
            ...(payload.size != null ? { size: payload.size } : {}),
        };

        const source$: Observable<ReportSdk.AdminTimesheetResponse> = this.shouldMock(
            'getAdminTimesheetsList',
        )
            ? of(
                adminReportsSdkMock.getAdminTimesheetsList({
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                    page: payload.page,
                    size: payload.size,
                }),
            ).pipe(delay(360))
            : this.adminService.getAdminTimesheetsList(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkAdminTimesheetResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-timesheets')),
        );
    }

    getAdminDownloadList(payload: AdminDownloadRequest): Observable<AdminDownloadResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-download');

        const sdkPayload: ReportSdk.AdminDownloadRequest = {
            page: payload.page ?? 0,
            size: payload.size ?? 10,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
            ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
            ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
            ...(payload.roleId != null ? { roleId: payload.roleId } : {}),
            ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
        };

        const source$: Observable<ReportSdk.AdminDownloadResponse> = this.shouldMock('getAdminDownloadList')
            ? of(
                adminReportsSdkMock.getAdminDownloadList({
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    roleId: payload.roleId,
                    search: payload.search,
                    page: payload.page,
                    size: payload.size,
                }),
            ).pipe(delay(360))
            : this.adminService.getAdminDownloadList(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkAdminDownloadResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-download')),
        );
    }

    getAdminDownloadEmployee(payload: AdminDownloadEmployeeRequest): Observable<AdminDownloadEmployeeResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-download-employee');

        const sdkPayload: ReportSdk.AdminDownloadEmployeeRequest = {
            employeeId: payload.employeeId,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
            ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
            ...(payload.page != null ? { page: payload.page } : {}),
            ...(payload.size != null ? { size: payload.size } : {}),
        };

        const source$ = this.shouldMock('getAdminDownloadEmployee')
            ? of(adminReportsSdkMock.getAdminDownloadEmployee(sdkPayload)).pipe(delay(360))
            : this.adminService.getAdminDownloadEmployee(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkAdminDownloadEmployeeResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-download-employee')),
        );
    }

    getEmployeeProductivity(payload: AdminProductivityRequest): Observable<EmployeeProductivityResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-employees');

        const sdkPayload = this.toSdkAdminProductivityRequest(payload);

        const source$ = this.shouldMock('getEmployeeProductivity')
            ? of(
                adminReportsSdkMock.getEmployeeProductivity({
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                    page: payload.page,
                    size: payload.size,
                }),
            ).pipe(delay(360))
            : this.adminService.getEmployeeProductivity(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkEmployeeProductivityResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-employees')),
        );
    }

    getOrganizationProductivity(payload: AdminProductivityRequest): Observable<OrganizationProductivityResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-org');

        const sdkPayload = this.toSdkAdminProductivityRequest(payload);

        const source$ = this.shouldMock('getOrganizationProductivity')
            ? of(
                adminReportsSdkMock.getOrganizationProductivity({
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                    page: payload.page,
                    size: payload.size,
                }),
            ).pipe(delay(360))
            : this.adminService.getOrganizationProductivity(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkOrganizationProductivityResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-org')),
        );
    }

    getEmployeeProductivityMetrics(payload: AdminProductivityRequest): Observable<EmployeeProductivityMetricResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-employees-metrics');

        const sdkPayload = this.toSdkAdminProductivityRequest(payload);

        const source$ = this.shouldMock('getEmployeeProductivityMetrics')
            ? of(adminReportsSdkMock.getEmployeeProductivityMetrics(sdkPayload)).pipe(delay(360))
            : this.adminService.getEmployeeProductivityMetrics(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkEmployeeProductivityMetricResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() =>
                this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-employees-metrics'),
            ),
        );
    }

    getOrganizationProductivityMetrics(
        payload: AdminProductivityRequest,
    ): Observable<LocationProductivityMetricResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-productivity-org-metrics');

        const sdkPayload = this.toSdkAdminProductivityRequest(payload);

        const source$ = this.shouldMock('getOrganizationProductivityMetrics')
            ? of(adminReportsSdkMock.getOrganizationProductivityMetrics(sdkPayload)).pipe(delay(360))
            : this.adminService.getOrganizationProductivityMetrics(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkLocationProductivityMetricResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-productivity-org-metrics')),
        );
    }

    getEmployeeDailyProductivity(payload: AdminProductivityRequest): Observable<EmployeeProductivityResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-daily-productivity');

        const sdkPayload = this.toSdkAdminProductivityRequest(payload);

        const source$ = this.shouldMock('getEmployeeDailyProductivity')
            ? of(adminReportsSdkMock.getEmployeeDailyProductivity(sdkPayload)).pipe(delay(360))
            : this.adminService.getEmployeeDailyProductivity(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkEmployeeProductivityResponseToDomain(resp)), // reutiliza mapper existente
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-daily-productivity')),
        );
    }

    getEmployeeDailyMetrics(payload: AdminProductivityRequest): Observable<EmployeeDailyMetricResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-daily-metrics');

        const sdkPayload = this.toSdkAdminProductivityRequest(payload);

        const source$ = this.shouldMock('getEmployeeDailyMetrics')
            ? of(adminReportsSdkMock.getEmployeeDailyMetrics(sdkPayload)).pipe(delay(360))
            : this.adminService.getEmployeeDailyMetrics(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkEmployeeDailyMetricResponseToDomain(resp)), // nuevo mapper
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-daily-metrics')),
        );
    }
    getAdminActivitiesSummary(
        payload: AdminActivitySummaryRequest,
    ): Observable<AdminActivitySummaryResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-activities-summary');

        const sdkPayload = toSdkAdminActivitySummaryRequest(payload);

        const source$: Observable<ReportSdk.AdminActivitySummaryResponse> = this.shouldMock(
            'getAdminActivitiesSummary',
        )
            ? of(
                adminReportsSdkMock.getAdminActivitiesSummary({
                    type: sdkPayload.type,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                }),
            ).pipe(delay(360))
            : this.adminService.getAdminActivitiesSummary(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkAdminActivitySummaryResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-activities-summary')),
        );
    }

    getAdminActivityDetail(
        payload: AdminActivityDetailRequest,
    ): Observable<AdminActivityDetailResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-admin-activity-detail');

        const sdkPayload = toSdkAdminActivityDetailRequest(payload);

        const source$: Observable<ReportSdk.AdminActivityDetailResponse> = this.shouldMock(
            'getAdminActivityDetail',
        )
            ? of(
                adminReportsSdkMock.getAdminActivityDetail({
                    type: sdkPayload.type,
                    identifier: payload.identifier,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                }),
            ).pipe(delay(360))
            : this.adminService.getAdminActivityDetail(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkAdminActivityDetailResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-admin-activity-detail')),
        );
    }

    exportAdminTimesheetsList(payload: AdminTimesheetExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-timesheets',
            'exportAdminTimesheetsList',
            () =>
                adminReportsSdkMock.exportAdminTimesheetsList({
                    format: payload.format,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminTimesheetExportRequest = {
                    format: payload.format as ReportSdk.ExportFormatEnum,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
                    ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                };
                return this.adminService.exportAdminTimesheetsList(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    exportAttendanceRecord(payload: AdminTimesheetExportRequest): Observable<Blob> {
        const url = `${this.env.host}${this.env.apiBasePath}/api/v1/report/admin/timesheets/attendance/export`;
        return this.runExport(
            'report-admin-attendance-record',
            'exportAdminTimesheetsList',
            () => adminReportsSdkMock.exportAdminTimesheetsList(payload),
            () => this.http.post(url, payload, {
                observe: 'response',
                responseType: 'blob',
            }),
        );
    }

    exportAdminDownloadList(payload: AdminDownloadListExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-download-list',
            'exportAdminDownloadList',
            () =>
                adminReportsSdkMock.exportAdminDownloadList({
                    format: payload.format,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    roleId: payload.roleId,
                    search: payload.search,
                    extraFields: payload.extraFields,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminDownloadExportRequest = {
                    format: payload.format as ReportSdk.ExportFormatEnum,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
                    ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
                    ...(payload.roleId != null ? { roleId: payload.roleId } : {}),
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                    ...(payload.extraFields?.length ? { extraFields: payload.extraFields } : {}),
                };
                return this.adminService.exportAdminDownloadList(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    exportAdminDownloadEmployee(payload: AdminDownloadEmployeeExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-download-employee',
            'exportAdminDownloadEmployee',
            () =>
                adminReportsSdkMock.exportAdminDownloadEmployee({
                    format: payload.format,
                    employeeId: payload.employeeId,
                    dateRange: payload.dateRange,
                    search: payload.search,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminDownloadEmployeeExportRequest = {
                    employeeId: payload.employeeId,
                    format: payload.format as ReportSdk.ExportFormatEnum,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                };
                return this.adminService.exportAdminDownloadEmployee(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    exportProductivityEmployees(payload: AdminProductivityExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-productivity-employees',
            'exportProductivityEmployees',
            () =>
                adminReportsSdkMock.exportProductivityEmployees({
                    format: payload.format,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    employeeId: payload.employeeId,
                    search: payload.search,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminProductivityExportRequest = {
                    format: payload.format as ReportSdk.ExportFormatEnum,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
                    ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
                    ...(payload.employeeId != null ? { employeeId: payload.employeeId } : {}),
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                };
                return this.adminService.exportProductivityEmployees(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    exportProductivityLocation(payload: AdminProductivityExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-productivity-location',
            'exportProductivityLocation',
            () =>
                adminReportsSdkMock.exportProductivityLocation({
                    format: payload.format,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminProductivityExportRequest = {
                    format: payload.format as ReportSdk.ExportFormatEnum,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
                    ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                };
                return this.adminService.exportProductivityLocation(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    exportProductivityDepartment(payload: AdminProductivityExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-productivity-department',
            'exportProductivityDepartment',
            () =>
                adminReportsSdkMock.exportProductivityDepartment({
                    format: payload.format,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminProductivityExportRequest = {
                    format: payload.format as ReportSdk.ExportFormatEnum,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
                    ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                };
                return this.adminService.exportProductivityDepartment(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    exportProductivityAll(payload: AdminProductivityExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-productivity-all',
            'exportProductivityAll',
            () =>
                adminReportsSdkMock.exportProductivityEmployees({
                    format: payload.format,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    search: payload.search,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminProductivityExportRequest = {
                    format: payload.format as ReportSdk.ExportFormatEnum,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                };
                return this.adminService.exportProductivityAll(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    exportWeeklyProductivity(payload: AdminProductivityExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-productivity-weekly',
            'exportWeeklyProductivity',
            () =>
                adminReportsSdkMock.exportProductivityEmployees({
                    format: payload.format,
                    dateRange: payload.dateRange,
                    locationId: payload.locationId,
                    departmentId: payload.departmentId,
                    employeeId: payload.employeeId,
                    search: payload.search,
                }),
            () => {
                const dr = payload.dateRange;
                const sdkPayload: ReportSdk.AdminProductivityExportRequest = {
                    format: ReportSdk.ExportFormatEnum.Excel,
                    dateRange: {
                        startDate: dr.startDate,
                        endDate: dr.endDate ?? dr.startDate,
                    },
                    ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
                    ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
                    ...(payload.employeeId != null ? { employeeId: payload.employeeId } : {}),
                    ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
                    ...(payload.ignoreHolidays != null
                        ? { ignoreHolidays: payload.ignoreHolidays }
                        : {}),
                };
                return this.adminService.exportWeeklyProductivity(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(AdminExportFormat.Excel),
                );
            },
            false,
        );
    }

    exportAdminActivities(payload: AdminActivityExportRequest): Observable<Blob> {
        return this.runExport(
            'report-admin-export-activities',
            'exportAdminActivities',
            () =>
                new Blob(
                    ['Tipo;Nombre / Dominio;Clasificación;Tiempo total;Fecha\n'],
                    { type: 'text/csv;charset=utf-8;' },
                ),
            () => {
                const sdkPayload = toSdkAdminActivityExportRequest(payload);
                return this.adminService.exportAdminActivities(
                    sdkPayload,
                    'response',
                    false,
                    this.reportExportHttpOptions(payload.format),
                );
            },
        );
    }

    private reportExportHttpOptions(
        format: AdminExportFormat | ReportSdk.ExportFormatEnum | string,
    ): Parameters<ReportSdk.AdminService['exportAdminTimesheetsList']>[3] {
        const f = String(format).toUpperCase();
        let accept: string;
        switch (f) {
            case String(AdminExportFormat.Csv):
                accept = 'text/csv, */*;q=0.8';
                break;
            case String(AdminExportFormat.Pdf):
                accept = 'application/pdf, */*;q=0.8';
                break;
            case String(AdminExportFormat.Excel):
                accept =
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, */*;q=0.8';
                break;
            default:
                accept = '*/*';
        }
        return { httpHeaderAccept: accept } as unknown as Parameters<
            ReportSdk.AdminService['exportAdminTimesheetsList']
        >[3];
    }

    private toSdkAdminProductivityRequest(payload: AdminProductivityRequest): ReportSdk.AdminProductivityRequest {
        return {
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate ?? payload.dateRange.startDate,
            },
            ...(payload.locationId != null ? { locationId: payload.locationId } : {}),
            ...(payload.departmentId != null ? { departmentId: payload.departmentId } : {}),
            ...(payload.employeeId   != null ? { employeeId:   payload.employeeId   } : {}),
            ...(payload.search?.trim() ? { search: payload.search.trim() } : {}),
            ...(payload.page != null ? { page: payload.page } : {}),
            ...(payload.size != null ? { size: payload.size } : {}),
        };
    }

    private runExport(
        loaderKey: string,
        mockAction: AdminReportsMockAction,
        mockBlob: () => Blob,
        realRequest: () => Observable<HttpResponse<unknown>>,
        notifyOnError = true,
    ): Observable<Blob> {
        this.appEvents.loaderVisiblePublish(true, loaderKey);

        const source$: Observable<Blob> = this.shouldMock(mockAction)
            ? of(mockBlob()).pipe(delay(360))
            : realRequest().pipe(map((res) => this.httpResponseToBlob(res)));

        return source$.pipe(
            catchError((err) => {
                if (notifyOnError) this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, loaderKey)),
        );
    }

    private httpResponseToBlob(res: HttpResponse<unknown>): Blob {
        const body = res.body;
        if (body instanceof Blob) return body;
        const type = res.headers.get('Content-Type') ?? undefined;
        return new Blob(body != null ? [body as BlobPart] : [], { type });
    }

    private shouldMock(action: AdminReportsMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.admin?.[action]);
    }

    private showErrorToast(err: unknown): void {
        if (!this.toast) return;
        const status = (err as { status?: number })?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
            503: 'Servicio temporalmente no disponible',
        };
        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status ?? 0] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
